import {
  Autocomplete,
  Box,
  Chip,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import zeplinColor from '../../../theme/Color';

const CustomAutocomplete = ({
  options,
  label,
  getOptionLabel,
  handleDeleteItems,
  selectedItems,
  handleSelection,
  value,
  multiple = true,
  handleFilter,
  disabled = false,
  placeholder = '',
  currentLength,
  totalLength,
  next,
  required = false,
  border,
  popupIcon,
  loading = false,
  inputBorder = 0,
  searchIcon,
  chip = false,
  groupBy = false,
  outlineInputPadding,
  fallback,
}: // ,renderTags
{
  options: { Name: string; Id: string }[];
  label: string;
  getOptionLabel: any;
  handleDeleteItems: (item: any, type: string) => void;
  selectedItems?:
    | {
        priority: string;
        status: string;
        related: { Name: string; Id: string }[];
        relatedType: string[];
        assignTo: { Name: string; Id: string }[];
        nameType: string[];
        name: { Name: string; Id: string }[];
        dueDate: string;
      }
    | { account: any[] }
    | any;
  value: any;
  disabled?: boolean;
  // renderTags:any[];
  handleSelection: (item: any) => void;
  multiple?: boolean;
  handleFilter: (e: React.SyntheticEvent, value: string) => void;
  placeholder?: string;
  currentLength: number;
  totalLength: number;
  next: () => void;
  required?: boolean;
  border?: any;
  popupIcon?: string;
  searchIcon?: string;
  loading?: boolean;
  inputBorder?: number;
  chip?: boolean;
  link?: boolean;
  groupBy?: boolean;
  outlineInputPadding?: string;
  fallback?: string;
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true); // Track if we are at the bottom
  const [categoryFilter, setCategoryFilter] = useState('All');

  // Get unique categories from options if groupBy is true
  const categories = groupBy
    ? Array.from(new Set(options.map((opt: any) => opt.group))).filter(Boolean)
    : [];

  const filteredOptions =
    groupBy && categoryFilter !== 'All'
      ? options.filter((opt: any) => opt.group === categoryFilter)
      : options;

  const handleScroll = () => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const isAtBottom =
        scrollContainer.scrollHeight - scrollContainer.scrollTop === scrollContainer.clientHeight;
      setIsAtBottom(isAtBottom);
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer && isAtBottom) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [currentLength, isAtBottom]);
  const [open, setOpen] = useState(false);

  const handleClose = (option?: any) => {
    console.log(option?.target, 'target');
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };
  return (
    <Autocomplete
      loading={loading}
      multiple={multiple}
      sx={{
        height: '48px',
        '.MuiAutocomplete-popupIndicatorOpen': { transform: 'none' },
        ...(border && { '.MuiOutlinedInput-notchedOutline': { border: 'none' } }),
        '& .MuiOutlinedInput-root': {
          ...(outlineInputPadding && { padding: outlineInputPadding }),
        },
      }}
      disabled={disabled ? true : false}
      onInputChange={handleFilter}
      popupIcon={
        popupIcon ? <img src={popupIcon} width={20} height={20} /> : '' //<img src='/assets/down-arrow.svg' width={20} height={20} />
      }
      value={multiple ? (value ? value : []) : value || null}
      options={options}
      disableClearable
      getOptionLabel={getOptionLabel}
      {...(!groupBy && { open: open })}
      {...(!groupBy && { onOpen: handleOpen })}
      {...(!groupBy && { onClose: handleClose })}
      {...(groupBy && {
        groupBy: (option: any) => option.group,
      })}
      {...(chip && {
        renderValue: (value: any) =>
          !Object.values(value) || !value?.Name ? (
            ''
          ) : (
            <Box
              sx={{
                borderRadius: '30px',
                padding: { xs: '4px 6px', xl: '6px 8px' },
                backgroundColor: zeplinColor.Neutral3,
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <img
                src={value?.avatar}
                alt={value?.Name}
                style={{ width: '24px', height: '24px', borderRadius: '20px', objectFit: 'cover' }}
              />
              <Typography variant='responsiveBody3'>{value?.Name}</Typography>
            </Box>
          ),
      })}
      size={isXs ? 'small' : 'medium'}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          slotProps={{
            inputLabel: { size: isXs ? 'small' : 'medium', sx: { fontSize: isXs ? 14 : 16 } },
          }}
          sx={{
            '& .MuiFormLabel-asterisk': {
              color: zeplinColor.Error,
            },
            ...(inputBorder && {
              '& .MuiInputBase-root': {
                borderRadius: `${inputBorder}px`,
              },
            }),
            '& input::placeholder': {
              fontSize: '16px', // Adjust the font size as needed
              [theme.breakpoints.down('xl')]: {
                fontSize: '14px', // Adjust the font size as needed
              },
            },
          }}
          label={chip ? '' : label}
          placeholder={value ? '' : placeholder ? placeholder : ''}
          InputProps={{
            ...params.InputProps,
            ...(chip && {
              startAdornment: (
                <>
                  <img src={searchIcon} width={20} height={20} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }),
            ...(chip &&
              value && {
                endAdornment: (
                  <>
                    {/* {params.InputProps.endAdornment} */}
                    <img
                      width={'18px'}
                      height={'18px'}
                      src={'/assets/x-mark.svg'}
                      alt='delete-icon'
                      onClick={(e) => {
                        handleDeleteItems([], 'captiv8');
                      }}
                      aria-hidden='false'
                      style={{ cursor: 'pointer' }}
                    />
                  </>
                ),
              }),
          }}
        />
      )}
      renderOption={(props, option) => (
        <MenuItem
          {...props}
          key={option.Id ? option?.Id : Object?.values(option)[0]}
          value={option.Id ? option?.Id : Object?.values(option)[0]}
          onClick={(e) => {
            handleClose();
            handleSelection(option);
          }}
          sx={{ textWrap: 'wrap', whiteSpace: 'wrap' }}
        >
          {option?.followers ? (
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                padding: '8px',
              }}
            >
              <Box
                sx={{
                  border: `1px solid ${zeplinColor.Neutral5}`,
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: zeplinColor.Neutral1,
                  width: '40px',
                  height: '40px',
                  padding: '8px',
                }}
              >
                <img
                  src={option?.avatar || ''}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = fallback || '';
                    e.currentTarget.style.width = '28px';
                    e.currentTarget.style.height = '28px';
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant='responsiveHeading4'>{option?.Name}</Typography>
                <Typography variant='responsiveBody3' color={zeplinColor?.Neutral8}>
                  {option?.followers}
                </Typography>
              </Box>
            </Box>
          ) : option?.Name ? (
            option?.Name
          ) : Object.keys(option)?.length ? (
            Object.keys(option)[0]
          ) : (
            option
          )}
        </MenuItem>
      )}
      slotProps={{
        listbox: {
          component: (listboxProps: any) => {
            const { children, ...rest } = listboxProps;
            console.log(rest);
            return (
              <>
                <InfiniteScroll
                  dataLength={currentLength}
                  next={next}
                  hasMore={currentLength < totalLength}
                  loader={
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <h4>Loading...</h4>
                    </Box>
                  }
                  height={groupBy ? '20rem' : '10rem'}
                  endMessage={<p style={{ textAlign: 'center' }}></p>}
                  scrollThreshold={0.9}
                  scrollableTarget='scrollableDiv'
                  {...listboxProps}
                >
                  <div
                    id='scrollableDiv'
                    ref={scrollContainerRef}
                    style={{
                      overflowY: 'auto',
                      height: '300px',
                    }}
                    {...rest}
                    onScroll={handleScroll}
                  >
                    {groupBy && (
                      <Box
                        sx={{ display: 'flex', gap: 1, p: 1, flexWrap: 'wrap' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        <Chip
                          label={<Typography variant='label1'>All</Typography>}
                          sx={{
                            backgroundColor:
                              categoryFilter === 'All'
                                ? zeplinColor.BasicBlack
                                : zeplinColor.Neutral3,
                            color:
                              categoryFilter === 'All'
                                ? zeplinColor.Neutral1
                                : zeplinColor.BasicBlack,
                            '&.MuiChip-root': {
                              padding: '16px 12px',
                              borderRadius: '50px',
                              '&:hover': {
                                backgroundColor:
                                  categoryFilter === 'All'
                                    ? zeplinColor.BasicBlack
                                    : zeplinColor.Neutral4,
                                color:
                                  categoryFilter === 'All'
                                    ? zeplinColor.Neutral1
                                    : zeplinColor.BasicBlack,
                              },
                            },

                            '& .MuiChip-label': {
                              padding: 0,
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCategoryFilter('All');
                          }}
                          clickable
                        />
                        {categories.map((cat) => (
                          <Chip
                            key={cat}
                            label={<Typography variant='label1'>{cat}</Typography>}
                            sx={{
                              backgroundColor:
                                categoryFilter === cat
                                  ? zeplinColor.BasicBlack
                                  : zeplinColor.Neutral3,
                              color:
                                categoryFilter === cat
                                  ? zeplinColor.Neutral1
                                  : zeplinColor.BasicBlack,
                              '&.MuiChip-root': {
                                padding: '16px 12px',
                                borderRadius: '50px',
                                '&:hover': {
                                  backgroundColor:
                                    categoryFilter === cat
                                      ? zeplinColor.BasicBlack
                                      : zeplinColor.Neutral4,
                                  color:
                                    categoryFilter === cat
                                      ? zeplinColor.Neutral1
                                      : zeplinColor.BasicBlack,
                                },
                              },
                              '& .MuiChip-label': {
                                padding: 0,
                              },
                            }}
                            onClick={(e) => {
                              setCategoryFilter(cat);
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    {children}
                  </div>
                </InfiniteScroll>
              </>
            );
          },

          children: (
            <>
              {options?.map((option) => (
                <MenuItem
                  key={option.Id}
                  value={option.Id}
                  sx={{
                    textWrap: 'wrap',
                    position: 'relative',
                  }}
                >
                  {option.Name}
                </MenuItem>
              ))}
            </>
          ),
          style: {
            ...(chip && { height: '16rem' }),
          },
        },
      }}
      renderTags={(selecteds) => (
        <Box sx={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
          {value?.length &&
            value?.map((selected: any) => (
              <Chip
                label={selected.Name}
                onDelete={(e) => {
                  handleDeleteItems(selected, 'assignTo');
                }}
                deleteIcon={
                  <img
                    width={'18px'}
                    height={'18px'}
                    src={'/assets/x-mark.svg'}
                    alt='delete-icon'
                    aria-hidden='false'
                  />
                }
                key={selected.Id}
              />
            ))}
        </Box>
      )}
    />
  );
};

export default CustomAutocomplete;
